import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestService } from '@df/api/request/request.service';
import { IAlternate, IModelDataV3WithUrl, Model } from '@df/catalog/model';
import { Route } from '@df/catalog/route/route';
import { App } from '@df/core/app';
import { Meta, MetaState } from '@df/core/meta/meta';
import { Multisite } from '@df/core/multisite/multisite.interface';
import { Events } from '@df/core/service/event';
import { MultisiteService } from '@df/core/service/multisite';
import { Url } from '@df/core/service/url';
import { Nexus } from '@tomandco/nexus-api-types';
import each from 'lodash-es/each';
import flatten from 'lodash-es/flatten';
import { filter } from 'rxjs/operators';

export interface IGetAlternateRequestEvent {
    request: Multisite.ILoadAlternateRequest;
    route?: Route;
}

export const GET_ALTENATE_REQUEST_EVENT = 'getAlternateRequest';

@Injectable({
    providedIn: 'root'
})
export class MetaAlternateService {
    /**
     * Map with store codes and store ids
     */
    private _langMap?: Multisite.ILangMap;

    /**
     * Added meta tags
     * @type {HTMLLinkElement[]}
     */
    private _links: HTMLLinkElement[] = [];

    /**
     * Last alternates
     */
    private _alternates?: IAlternate[];

    /**
     * Last processed model id
     */
    private currentModelId?: number | string;

    private renderer: Renderer2;

    private existingAlternates = new Map<Model, IAlternate[]>();

    protected meta = inject(Meta);
    private multisiteService = inject(MultisiteService);
    private app = inject(App);
    private url = inject(Url);
    private events = inject(Events);
    private request = inject(RequestService);
    private document = inject(DOCUMENT);
    private destroyRef = inject(DestroyRef);

    constructor() {
        this.renderer = inject(RendererFactory2).createRenderer(null, null);

        // Subscribe to meta changes
        this.meta.subject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((metaState?: MetaState) => !!metaState?.model)
            )
            .subscribe(() => this.updateAlternates(this.meta.model));

        if (this.meta.model) {
            this.updateAlternates(this.meta.model);
        }
    }

    /**
     * When creating `alternate` meta tags (different language versions) we need to provide
     *
     * <link rel="alternate" hreflang="en-gb" href="http://www.musicradar.com/" />
     * <link rel="alternate" hreflang="x-default" href="http://www.musicradar.com/" />
     * <link rel="alternate" hreflang="en-us" href="http://www.musicradar.com/us/" />
     * <link rel="alternate" hreflang="fr" href="http://www.musicradar.com/fr/" />
     *
     * Note that we have 2 version of en so we specify with details
     * at the same time we have 1 version of fr so we only provide short version
     *
     */
    private get langMap(): Multisite.ILangMap {
        if (!this._langMap) {
            const stores = this.multisiteService.all;

            if (stores && stores.length > 0) {
                const map: { [langCode: string]: { store: number; code: string } } = {};
                const countsIndex: { [langGroup: string]: number } = {};
                const langGroup = this.multisiteService.current.langGroup;
                // we group
                stores
                    .filter(store => store.langGroup === langGroup)
                    .forEach(store => {
                        store.lang?.forEach(langCode => {
                            const langGroup = langCode.split('-')[0];
                            const current = map[langCode];
                            map[langCode] = { store: current ? Math.min(current.store, store.id) : store.id, code: langGroup };
                            // create counts
                            if (!countsIndex[langGroup]) {
                                countsIndex[langGroup] = 0;
                            }
                            countsIndex[langGroup]++;
                        });
                    });

                const langMap: Multisite.ILangMap = {};
                each(map, (conf, langCode) => {
                    if (!langMap[conf.store]) {
                        langMap[conf.store] = [];
                    }
                    langMap[conf.store].push(countsIndex[conf.code] > 1 || conf.code === 'x' ? langCode : conf.code);
                });

                this._langMap = langMap;
            } else {
                this._langMap = {};
            }
        }
        return this._langMap;
    }

    /**
     * Takes current store alternate data for given model
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {Model} model
     */
    private getCurrentStoreAlternates(model: Model<IModelDataV3WithUrl>): IAlternate[] | undefined {
        const storeId = this.multisiteService.storeId;
        const currentStoreLangs = this.langMap[storeId];

        if (currentStoreLangs?.length && model.data) {
            const url = this.getMetaCanonical(model);

            if (url) {
                const href = this.url.toUrl(url);
                return currentStoreLangs.map(lang => ({
                    lang,
                    href,
                    storeId
                }));
            }
        }

        return undefined;
    }

    protected getMetaCanonical(model: Model<IModelDataV3WithUrl>): string | undefined {
        return this.meta.getMetaProperty('canonical', model) || model.data?.url;
    }

    /**
     * In this method we try to use alternates directly provides in meta
     * that is forced mode, typically used with WP pages
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    private getStaticModelAlternates(model: Model<IModelDataV3WithUrl>): IAlternate[] | undefined {
        return model.data && model.data.meta && model.data.meta.alternates;
    }

    /**
     * Called on meta changes
     * Cleans up any previous processing and sets new alternates for model
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {Model} model [description]
     */
    private updateAlternates(model?: Model<IModelDataV3WithUrl>): void {
        // do not apply if last processed model is requested one
        if (this.currentModelId && this.currentModelId === model?.id) {
            return;
        }

        // cleanup
        this.removeAlternates();

        if (model && !['search'].includes(model.type)) {
            if (this.existingAlternates.has(model)) {
                this.setAlternates(model, this.existingAlternates.get(model));
                return;
            }

            this.currentModelId = model.id;
            // try to use manual
            const staticAlternates = this.getStaticModelAlternates(model);

            if (staticAlternates) {
                this.setAlternates(model, [...staticAlternates]);
            } else {
                const storesIds = this.multisiteService.other.map(store => store.id);

                if (storesIds.length) {
                    const event: IGetAlternateRequestEvent = {
                        request: { id: <number>model.id, type: model.type, storesIds },
                        route: this.meta.state?.route
                    };
                    this.events.broadcast(GET_ALTENATE_REQUEST_EVENT, event);
                    this.request.send(
                        undefined,
                        () => this.loadFromApi(event.request),
                        (result: Multisite.ILoadAlternateResult | undefined) => {
                            // only apply if stil valid
                            if (result && this.currentModelId === model.id) {
                                this.setAlternates(
                                    model,
                                    flatten(
                                        result.alternates
                                            ?.map((alt): IAlternate[] | undefined => {
                                                if (this.langMap[alt.store]) {
                                                    const href = this.url.toUrl(alt.href, alt.store);
                                                    if (href) {
                                                        return this.langMap[alt.store].map(
                                                            (lang: string): IAlternate => ({
                                                                href,
                                                                lang,
                                                                storeId: alt.store
                                                            })
                                                        );
                                                    }
                                                }
                                                return undefined;
                                            })
                                            .filter(i => !!i) as IAlternate[] | IAlternate[][]
                                    )
                                );
                            }
                        }
                    );
                } else {
                    this.setAlternates(model);
                }
            }
        } else {
            this.currentModelId = undefined;
        }
    }

    protected async loadFromApi(request: Nexus.Api.Catalog.FindAlternate.Request): Promise<Nexus.Api.Catalog.FindAlternate.Response> {
        return this.request.nexus.catalog.findAlternate(request);
    }

    /**
     * Sets alternate links in head
     */
    private setAlternates(model: Model<IModelDataV3WithUrl>, alternates: IAlternate[] = []) {
        if (model) {
            this.existingAlternates.set(model, alternates);
        }

        const current = this.getCurrentStoreAlternates(model);
        if (current) {
            alternates.push(...current);
        }

        // mark processed to prevent duplicates
        const processedLangs: string[] = [];

        alternates.forEach(alternate => {
            if (processedLangs.indexOf(alternate.lang) === -1) {
                processedLangs.push(alternate.lang);
                const link = this.renderer.createElement('link');
                link.setAttribute('rel', 'alternate');
                link.setAttribute('hreflang', alternate.lang);
                link.setAttribute('href', alternate.href);
                if (!this.app.isPlatformBrowser) {
                    link.setAttribute('un-app-link');
                }
                this.renderer.appendChild(this.document.head, link);
                this._links.push(link);
            }
        });

        this._alternates = alternates;

        // broadcast event
        const event: Multisite.IMetaAlternateUpdateEvent = {};
        this.events.broadcast(Multisite.META_ALTERNATE_UPDATE_EVENT, event);
    }

    /**
     * Removes existing alternate links
     */
    private removeAlternates() {
        let link: HTMLLinkElement | undefined;
        do {
            link = this._links.shift();
            if (link) {
                this.renderer.removeChild(this.document.head, link);
            }
        } while (link);
    }

    /**
     * Retrieve current alternates
     */
    getAlternates() {
        return this._alternates;
    }
}
